import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Megaphone, Gift, Clock, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =============================================
// PROMO BANNER - Bandeau promotionnel
// Visible uniquement par les clients
// Gérable depuis ADMIN > Commerce > Bandeau promo
// =============================================

const PromoBanner = ({ forceShow = false }) => {
  const { isClient } = useAuth();
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Show for clients or when forceShow is true (used in ClientDashboard)
  const shouldFetch = isClient || forceShow;

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${API}/settings/promo-banner`);
        if (response.data && response.data.enabled) {
          // Check if banner is within date range
          const now = new Date();
          const startDate = response.data.startDate ? new Date(response.data.startDate) : null;
          const endDate = response.data.endDate ? new Date(response.data.endDate) : null;
          
          if ((!startDate || now >= startDate) && (!endDate || now <= endDate)) {
            // Map backend fields to component expected format
            setBanner({
              ...response.data,
              message: response.data.title,
              background_color: response.data.backgroundColor,
              text_color: response.data.titleColor,
              promo_code: response.data.subtitle?.replace('Code: ', '') || null,
              cta_text: response.data.linkText,
              cta_link: response.data.link
            });
          }
        }
      } catch (error) {
        console.log('No promo banner available');
      } finally {
        setLoading(false);
      }
    };

    // Fetch for clients or when forceShow is true
    if (shouldFetch) {
      // Check if already dismissed in this session
      const dismissedBanner = sessionStorage.getItem('promoBannerDismissed');
      if (!dismissedBanner) {
        fetchBanner();
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [shouldFetch]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('promoBannerDismissed', 'true');
  };

  // Only show for clients (or forceShow) with active banner
  if ((!isClient && !forceShow) || loading || dismissed || !banner) {
    return null;
  }

  const bgStyles = {
    gradient: 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500',
    solid: `bg-[${banner.background_color || '#f97316'}]`,
    image: banner.background_image ? `bg-cover bg-center` : 'bg-gradient-to-r from-orange-500 to-red-500'
  };

  const bgStyle = banner.banner_type === 'image' && banner.background_image 
    ? { backgroundImage: `url(${banner.background_image})` }
    : banner.background_color 
      ? { backgroundColor: banner.background_color }
      : {};

  return (
    <div 
      className={cn(
        "w-full py-2.5 px-4",
        !banner.background_image && !banner.background_color && bgStyles.gradient
      )}
      style={bgStyle}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          {banner.icon === 'gift' ? (
            <Gift className="w-5 h-5 text-white" />
          ) : banner.icon === 'clock' ? (
            <Clock className="w-5 h-5 text-white" />
          ) : (
            <Megaphone className="w-5 h-5 text-white" />
          )}
        </div>

        {/* Message */}
        <p 
          className="text-white text-sm font-medium text-center"
          style={{ color: banner.text_color || '#ffffff' }}
        >
          {banner.message || 'Offre spéciale en cours !'}
          {banner.promo_code && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs font-bold">
              Code: {banner.promo_code}
            </span>
          )}
        </p>

        {/* CTA Button */}
        {banner.cta_text && banner.cta_link && (
          <a
            href={banner.cta_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-white text-xs font-medium transition-colors"
          >
            {banner.cta_text}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Fermer"
        >
          <X className="w-4 h-4 text-white/80 hover:text-white" />
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
